import type { APISharedProfileResponse } from '@see-stella/types'
import { useMemo, useReducer } from 'react'
import { collectScans, type Scan } from './model'

/**
 * The shared interactive state for the report. ONE metric is selected at a time
 * (a global switcher, not a multi-select): the photo, the chart, and the metric
 * definition all follow it. The scan cursor (`scanIdx`) drives the photo AND the
 * chart together, and `playing` runs the time-lapse. Kept in one reducer so every
 * transition is atomic.
 */
export interface ReportState {
  scans: Scan[]
  /** index into `scans`, oldest → newest; defaults to the latest scan */
  scanIdx: number
  /** the single selected metric techName (drives photo + chart + definition) */
  metric: string
  /** whether the time-lapse is running */
  playing: boolean
}

export interface ReportActions {
  setScanIdx: (i: number) => void
  setMetric: (techName: string) => void
  togglePlay: () => void
  setPlaying: (value: boolean) => void
}

export type Core = Omit<ReportState, 'scans'>

export type Action =
  | { type: 'scan'; idx: number }
  | { type: 'metric'; techName: string }
  | { type: 'togglePlay'; lastIdx: number }
  | { type: 'setPlaying'; value: boolean }

/** Exported for unit testing; the hook wires it into useReducer. */
export function reducer(state: Core, action: Action): Core {
  switch (action.type) {
    case 'scan':
      return { ...state, scanIdx: action.idx }
    case 'metric':
      // Switching the metric never moves the scan cursor — the provider keeps the
      // same moment in time and just changes which signal they're reading.
      return state.metric === action.techName
        ? state
        : { ...state, metric: action.techName }
    case 'togglePlay': {
      const playing = !state.playing
      // Starting playback from the last scan restarts from the beginning so the
      // time-lapse actually plays rather than sitting at the end.
      const scanIdx =
        playing && state.scanIdx >= action.lastIdx ? 0 : state.scanIdx
      return { ...state, playing, scanIdx }
    }
    case 'setPlaying':
      return state.playing === action.value
        ? state
        : { ...state, playing: action.value }
  }
}

export function useReportState(
  profile: APISharedProfileResponse
): [ReportState, ReportActions] {
  const scans = useMemo(() => collectScans(profile), [profile])
  const firstMetric = profile.metrics[0]?.techName ?? ''

  const [core, dispatch] = useReducer(reducer, undefined, () => ({
    scanIdx: Math.max(0, scans.length - 1),
    metric: firstMetric,
    playing: false,
  }))

  const actions: ReportActions = useMemo(
    () => ({
      setScanIdx: (i) =>
        dispatch({
          type: 'scan',
          idx: Math.max(0, Math.min(scans.length - 1, i)),
        }),
      setMetric: (techName) => dispatch({ type: 'metric', techName }),
      togglePlay: () =>
        dispatch({ type: 'togglePlay', lastIdx: scans.length - 1 }),
      setPlaying: (value) => dispatch({ type: 'setPlaying', value }),
    }),
    [scans.length]
  )

  return [{ scans, ...core }, actions]
}
